import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import { filterOffersChoice } from "@/utils/redemption-helper";
import RedemptionSheetChip from "./RedemptionSheetChip";

interface RedemptionFilterSheetProps {
  open: boolean;
  onClose: () => void;
  /** GFG gift categories on offer (see availableGiftCategories). */
  categories: string[];
  /** Categories currently picked (multi-select); empty = all. */
  activeCats: string[];
  /** Toggle one category on/off. */
  onChooseCat: (value: string) => void;
  /** Clear every category pick (the "All categories" chip). */
  onClearCats: () => void;
  causes: string[];
  /** Causes currently picked (multi-select); empty = all. */
  activeCauses: string[];
  /** Toggle one cause on/off. */
  onChooseCause: (value: string) => void;
  /** Clear every cause pick (the "All causes" chip). */
  onClearCauses: () => void;
  favOnly: boolean;
  onToggleFav: () => void;
  onClearFilters: () => void;
  /** Live count of gifts matching the current filters — the "Show N gifts" CTA. */
  filteredCount: number;
}

/**
 * Mobile filter drawer — the touch form of the desktop Category/Cause dropdowns +
 * Favorites toggle. Built on MUI `Drawer` (anchor "bottom"), matching the
 * prototype's `.fsheet-*`: clear-dust surface, 20px rounded top, grabber, a
 * scrolling body with a PINNED head + foot (only the body scrolls). The Drawer
 * owns the backdrop, focus trap, Escape, and body-scroll lock. Only the mobile layout
 * renders it — RedemptionCollection gates it on `isMobile`.
 */
export default function RedemptionFilterSheet({
  open,
  onClose,
  categories,
  activeCats,
  onChooseCat,
  onClearCats,
  causes,
  activeCauses,
  onChooseCause,
  onClearCauses,
  favOnly,
  onToggleFav,
  onClearFilters,
  filteredCount,
}: RedemptionFilterSheetProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-label": "Filter gifts",
          sx: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "82vh",
            borderRadius: "20px 20px 0 0",
            bgcolor: "var(--color-clear-dust)",
            pb: "max(16px, env(safe-area-inset-bottom))",
          },
        },
      }}
    >
      {/* Grabber */}
      <div className="mx-auto mt-2.5 mb-1 h-1 w-10 shrink-0 rounded-full bg-smoke" />

      {/* Head (pinned) */}
      <div className="flex shrink-0 items-center justify-between px-5 pt-1.5 pb-3">
        <h2 className="font-crimson text-[22px] text-rodeo-black">Filter gifts</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="relative flex h-8.5 w-8.5 items-center justify-center rounded-full bg-wax text-graphite after:absolute after:-inset-[5px] after:content-['']"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Body (scrolls) */}
      <div className="flex min-h-0 flex-1 flex-col gap-5.5 overflow-y-auto px-5 pt-1 pb-2">
        {/* Category / Cause each render only when they offer a real choice (≥2 options) —
            on the Charity/Experiences tabs the lone option is already implied by the tab. */}
        {filterOffersChoice(categories) && (
          <SheetGroup label="Category">
            <RedemptionSheetChip selected={activeCats.length === 0} onClick={onClearCats}>
              All categories
            </RedemptionSheetChip>
            {categories.map((c) => (
              <RedemptionSheetChip
                key={c}
                selected={activeCats.includes(c)}
                onClick={() => onChooseCat(c)}
              >
                {c}
              </RedemptionSheetChip>
            ))}
          </SheetGroup>
        )}

        {filterOffersChoice(causes) && (
          <SheetGroup label="Cause">
            <RedemptionSheetChip selected={activeCauses.length === 0} onClick={onClearCauses}>
              All causes
            </RedemptionSheetChip>
            {causes.map((c) => (
              <RedemptionSheetChip
                key={c}
                selected={activeCauses.includes(c)}
                onClick={() => onChooseCause(c)}
              >
                {c}
              </RedemptionSheetChip>
            ))}
          </SheetGroup>
        )}

        <section className="flex items-center justify-between gap-4">
          <div>
            <div className="font-lato text-[11px] font-bold uppercase tracking-[1.5px] text-dust-ink">
              Favorites
            </div>
            <div className="mt-0.75 font-lato text-[13px] text-graphite">
              Show only gifts you've saved
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={favOnly}
            aria-label="Show favorites only"
            onClick={onToggleFav}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors after:absolute after:inset-x-0 after:-top-[8px] after:-bottom-[8px] after:content-[''] ${
              favOnly ? "bg-dark-dust" : "bg-smoke"
            }`}
          >
            <span
              className={`absolute top-0.75 h-5.5 w-5.5 rounded-full bg-white shadow transition-all ${
                favOnly ? "left-5.75" : "left-0.75"
              }`}
            />
          </button>
        </section>
      </div>

      {/* Foot (pinned) */}
      <div className="flex shrink-0 items-center gap-3.5 border-t border-card-border px-5 pt-3.5 pb-1.5">
        <button
          type="button"
          onClick={onClearFilters}
          className="shrink-0 cursor-pointer px-1 py-2.75 font-lato text-[14px] font-semibold text-graphite transition-colors hover:text-rodeo-black"
        >
          Clear all
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 cursor-pointer rounded-full bg-rodeo-black px-4 py-3.5 font-lato text-[14px] font-bold tracking-[0.3px] text-white transition-colors hover:bg-background-dark"
        >
          Show {filteredCount} gifts
        </button>
      </div>
    </Drawer>
  );
}

function SheetGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="font-lato text-[11px] font-bold uppercase tracking-[1.5px] text-dust-ink">
        {label}
      </div>
      <div className="flex flex-wrap gap-2.25">{children}</div>
    </section>
  );
}
